import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import styled, { css } from "styled-components";

import FilterChip from "../../../components/filter-chip/FilterChip";
import ToolTip from "../../../components/tooltip/ToolTip";
import { MaOptionSlot } from "./maTypes";

/**
 * MA オプションスロット（`(x)` / `[x]` マーカー）の候補切替チップ。
 *
 * タイムライン編集ドロワー（MaEditDrawer）と、通常プレビューの
 * 情報欄（Preview.tsx / メディアアイテム名の上）で共用する。
 * - ドロワー（default）: 外枠付きチップ + 結合ボタン。基本スタイル
 *   （Chip / ChipLabel / Segmented / SegmentButton）もここで定義し、
 *   MaEditDrawer のバリアント切替チップと見た目を揃える
 * - プレビュー（overlay）: 1 オプション 1 行。オプション名 + 候補ごとの FilterChip
 */

/** overlay 表示で再生 / 停止に合わせて畳む・広げるアニメーション */
const COLLAPSE_TRANSITION = { duration: 0.25, ease: "easeOut" } as const;

/** 候補が 2 つ以上あり、ユーザーが切り替えられるスロットか（1 候補のみならチップを出さない） */
export function isSelectableOptionSlot(slot: MaOptionSlot): boolean {
  return slot.choices.length >= 2;
}

export const Chip = styled.div<{
  $draggable: boolean;
  $dragging?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  height: 28px;
  padding: 0 10px 0 8px;
  border: 1px solid var(--soremo-border, #d9dcde);
  border-radius: 14px;
  background: #fff;
  cursor: ${(p) => (p.$draggable ? "grab" : "default")};
  opacity: ${(p) => (p.$dragging ? 0.35 : 1)};
  user-select: none;
  touch-action: none;

  ${(p) =>
    p.$draggable &&
    css`
      &:hover {
        border-color: var(--soremo-blue, #009ace);
      }
    `}

  .material-symbols-rounded {
    font-size: 16px;
    color: var(--soremo-light-gray, #a7a8a9);
  }
`;

export const ChipLabel = styled.span`
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid var(--soremo-border, #d9dcde);
  border-radius: 10px;
  overflow: hidden;
`;

export const SegmentButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  margin: 0;
  padding: 1px 8px;
  font-size: 11px;
  line-height: 16px;
  cursor: pointer;
  background: ${(p) => (p.$active ? "var(--soremo-blue, #009ace)" : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "var(--soremo-gray, #53565a)")};
  transition: background 0.15s, color 0.15s;

  & + & {
    border-left: 1px solid var(--soremo-border, #d9dcde);
  }

  ${(p) =>
    !p.$active &&
    css`
      &:hover:not(:disabled) {
        background: rgba(0, 154, 206, 0.1);
        color: var(--soremo-blue, #009ace);
      }
    `}
`;

/** オプション候補ボタン（ドロワー用。候補名が長い場合は省略表示） */
const OptionSegmentButton = styled(SegmentButton)`
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// ===== プレビュー上（overlay）用 =====

/** オプション 1 件分の行（先頭にオプション名、続けて候補ごとの FilterChip を並べる） */
const OverlayChipRow = styled.div`
  min-width: 0;
  max-width: 100%;
`;

/** 白い text-shadow（親の情報欄から継承）が省略表示用の overflow で切れないよう、この幅だけ内側に余白を取って外側へ打ち消す */
const OVERLAY_LABEL_GLOW_PX = 8;
/** オプション名と先頭チップの間隔 */
const OVERLAY_LABEL_GAP_PX = 8;
/** 候補チップ同士の間隔 */
const OVERLAY_CHOICE_GAP_PX = 4;

/** オプション名（再生中は幅 0 へ畳む） */
const OverlayLabel = styled(motion.span)`
  flex-shrink: 0;
  max-width: 160px;
  padding: ${OVERLAY_LABEL_GLOW_PX}px;
  margin: -${OVERLAY_LABEL_GLOW_PX}px 0 -${OVERLAY_LABEL_GLOW_PX}px -${OVERLAY_LABEL_GLOW_PX}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * 候補チップの外枠。行幅が足りない場合は縮んで候補名を省略表示する。
 * $clip: 幅 0 へ畳んだ状態・畳む / 広げるアニメーション中のみはみ出しを隠す
 * （常時隠すと FilterChip のホバー影が切れるため）
 */
const OverlayChoiceCollapse = styled(motion.div)<{ $clip: boolean }>`
  flex: 0 1 auto;
  min-width: 0;
  overflow: ${(p) => (p.$clip ? "hidden" : "visible")};
`;

const OverlayFilterChip = styled(FilterChip)`
  max-width: 100%;
  text-shadow: none;
`;

const OverlayChipText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * 候補ラベル群の共通接頭辞を表示用に取り除く（文字単位のフォールバック）。
 * 例: 「効果音A弱」「効果音A強」→「弱」「強」
 * カットすると空になる候補がある場合（ラベル自体が接頭辞のもの）はカットしない。
 */
function trimCommonLabelPrefixChars(labels: string[]): string[] {
  // サロゲートペア（絵文字等）を壊さないようコードポイント単位で比較する
  const chars = labels.map((label) => Array.from(label));
  const minLen = Math.min(...chars.map((c) => c.length));
  let prefixLen = 0;
  while (
    prefixLen < minLen &&
    chars.every((c) => c[prefixLen] === chars[0][prefixLen])
  ) {
    prefixLen += 1;
  }
  if (prefixLen === 0) return labels;
  if (chars.some((c) => c.length === prefixLen)) return labels;
  return chars.map((c) => c.slice(prefixLen).join(""));
}

/**
 * 候補ラベル群から共通部分を表示用に取り除く（UI 表示のみ。キー・aria-label は完全名）。
 *
 * ファイル名由来のラベルは `_` 区切りのトークン列（例:
 * `LKK3_SE_6707ACAI_0001_カットイン_助格SD_a`）になっているため、全候補で
 * トークン数が同じ場合はトークン列単位で間引く:
 * - 全候補で同値の列（プロジェクト名・用途等）を落とす
 * - 数字のみの列（連番）も落とす。ただし落とすと候補同士の区別が
 *   失われる（連番だけが相違）場合は残す
 * 例: 「LKK3_SE_…_0001_カットイン_助格SD_a」「LKK3_VO_…_0008_カットイン_弥七1_a」
 * → 「SE_助格SD」「VO_弥七1」
 *
 * トークン数が揃わない場合は文字単位の共通接頭辞カットへフォールバックする。
 * いずれも空ラベルや区別の消失が生じる場合はカットしない。
 */
export function shortenOptionChoiceLabels(labels: string[]): string[] {
  if (labels.length < 2) return labels;

  const tokenLists = labels.map((label) => label.split("_"));
  const tokenCount = tokenLists[0].length;
  const sameTokenCount = tokenLists.every((t) => t.length === tokenCount);
  if (sameTokenCount && tokenCount >= 2) {
    const distinctCount = new Set(labels).size;
    /** 残す列番号でラベルを組み直し、区別と非空が保たれる場合のみ採用 */
    const tryKeep = (keep: number[]): string[] | null => {
      if (keep.length === 0) return null;
      const shortened = tokenLists.map((t) => keep.map((col) => t[col]).join("_"));
      if (shortened.some((s) => s.length === 0)) return null;
      if (new Set(shortened).size !== distinctCount) return null;
      return shortened;
    };
    const differingCols: number[] = [];
    const differingNonNumericCols: number[] = [];
    for (let col = 0; col < tokenCount; col += 1) {
      const values = tokenLists.map((t) => t[col]);
      if (values.every((v) => v === values[0])) continue;
      differingCols.push(col);
      if (!values.every((v) => /^\d+$/.test(v))) differingNonNumericCols.push(col);
    }
    // 数字のみの列（連番）も落とした形を優先し、区別が失われるなら連番を残す
    const shortened = tryKeep(differingNonNumericCols) ?? tryKeep(differingCols);
    if (shortened) return shortened;
    return labels;
  }

  return trimCommonLabelPrefixChars(labels);
}

/** メディア上でも読めるよう白を濃くしたツールチップ（左サイドバーのサムネイルと共通） */
const TOOLTIP_THEME = "sidebar-thumb";

/** overlay の候補チップ 1 件（再生中は選択中以外を幅 0 へ畳む） */
function OverlayChoiceChip({
  label,
  active,
  collapsed,
  spaced,
  onClick,
}: {
  label: string;
  active: boolean;
  collapsed: boolean;
  /** 右隣のチップとの間隔を空けるか（畳む時は 0 へアニメーションさせる） */
  spaced: boolean;
  onClick?: () => void;
}) {
  // 畳む / 広げる幅アニメーション中だけはみ出しを隠す（間隔だけ変わる選択中チップは隠さない）
  const [prevCollapsed, setPrevCollapsed] = useState(collapsed);
  const [resizing, setResizing] = useState(false);
  if (prevCollapsed !== collapsed) {
    setPrevCollapsed(collapsed);
    setResizing(true);
  }

  return (
    <OverlayChoiceCollapse
      $clip={collapsed || resizing}
      inert={collapsed}
      initial={false}
      animate={{
        width: collapsed ? 0 : "auto",
        marginRight: spaced ? OVERLAY_CHOICE_GAP_PX : 0,
        opacity: collapsed ? 0 : 1,
      }}
      transition={COLLAPSE_TRANSITION}
      onAnimationComplete={() => setResizing(false)}
      // クリックでフォーカスを残すと、再生のスペースキーがチップの押下として扱われてフォーカス枠が出るため
      onMouseDown={(e) => e.preventDefault()}
    >
      <ToolTip description={label} truncatedOnly theme={TOOLTIP_THEME}>
        <OverlayFilterChip selected={active} onClick={onClick}>
          <OverlayChipText>{label}</OverlayChipText>
        </OverlayFilterChip>
      </ToolTip>
    </OverlayChoiceCollapse>
  );
}

export interface MaOptionSlotChipProps {
  slot: MaOptionSlot;
  /** 選択中候補キー（未選択時は呼び出し側で defaultChoiceKey を渡す） */
  selectedKey: string;
  onSelectOption?: (_slotKey: string, _choiceKey: string) => void;
  disabled?: boolean;
  /**
   * "overlay": プレビュー上に重ねる表示（1 オプション 1 行で並べる前提で外枠を持たず、
   *   行頭のオプション名に続けて候補ごとに FilterChip を並べる）。
   * "default": ドロワー内の表示（外枠 + オプション名 + 結合ボタン。
   *   バリアントチップと見分けるためアイコンを残す）
   */
  variant?: "default" | "overlay";
  /**
   * overlay 時のみ有効。true のときオプション名と選択中以外の候補チップを畳み、
   * 選択中の候補だけを表示する（再生中に動画との重なりを減らす）
   */
  activeOnly?: boolean;
}

/** オプションスロットチップ（`(x)` マーカーの候補切替。ドラッグ不可） */
export function MaOptionSlotChip({
  slot,
  selectedKey,
  onSelectOption,
  disabled,
  variant = "default",
  activeOnly = false,
}: MaOptionSlotChipProps) {
  // 長いファイル名対策: 候補間で共通する部分は表示上のみカットする
  const displayLabels = useMemo(
    () => shortenOptionChoiceLabels(slot.choices.map((c) => c.label)),
    [slot.choices],
  );

  if (variant === "overlay") {
    // 選択キーが候補に見つからない場合は全候補を出して切替できる状態を保つ
    const condensed = activeOnly && slot.choices.some((c) => c.key === selectedKey);
    return (
      <OverlayChipRow className="u-row" onPointerDown={(e) => e.stopPropagation()}>
        <ToolTip description={slot.name} truncatedOnly theme={TOOLTIP_THEME}>
          <OverlayLabel
            className="bodytext-11 u-text-black"
            aria-hidden={condensed || undefined}
            initial={false}
            animate={{
              width: condensed ? 0 : "auto",
              marginRight: condensed
                ? -OVERLAY_LABEL_GLOW_PX
                : OVERLAY_LABEL_GAP_PX - OVERLAY_LABEL_GLOW_PX,
              opacity: condensed ? 0 : 1,
            }}
            transition={COLLAPSE_TRANSITION}
          >
            {slot.name}
          </OverlayLabel>
        </ToolTip>
        {slot.choices.map((choice, index) => (
          <OverlayChoiceChip
            key={choice.key}
            label={displayLabels[index]}
            active={choice.key === selectedKey}
            collapsed={condensed && choice.key !== selectedKey}
            spaced={!condensed && index < slot.choices.length - 1}
            onClick={disabled ? undefined : () => onSelectOption?.(slot.key, choice.key)}
          />
        ))}
      </OverlayChipRow>
    );
  }

  return (
    <Chip $draggable={false}>
      <span className="material-symbols-rounded">alt_route</span>
      <ToolTip description={slot.name} truncatedOnly theme={TOOLTIP_THEME}>
        <ChipLabel className="bodytext-11 u-text-black">{slot.name}</ChipLabel>
      </ToolTip>
      <Segmented onPointerDown={(e) => e.stopPropagation()}>
        {slot.choices.map((choice, index) => (
          <ToolTip
            key={choice.key}
            description={displayLabels[index]}
            truncatedOnly
            theme={TOOLTIP_THEME}
          >
            <OptionSegmentButton
              type="button"
              aria-label={choice.label}
              $active={choice.key === selectedKey}
              disabled={disabled}
              onClick={() => onSelectOption?.(slot.key, choice.key)}
            >
              {displayLabels[index]}
            </OptionSegmentButton>
          </ToolTip>
        ))}
      </Segmented>
    </Chip>
  );
}
